import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrForm } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { Base64Service } from 'src/app/services/base64/base64.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { CameraComponent } from '../../_shared/camera/camera.component';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroupOfType, Key } from 'src/app/types/shared';
import { errorHandle } from 'src/app/misc/functions';
import * as ExcelJS from 'exceljs';

@Component({
  selector: 'app-bahnuebergangsdialog',
  templateUrl: './bahnuebergangsdialog.component.html',
  styleUrls: ['./bahnuebergangsdialog.component.scss'],
  imports: [ClarityModule, FormsModule, ReactiveFormsModule, CameraComponent, MrTranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class BahnuebergangsdialogComponent {
  private readonly apiservice = inject(APIService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly base64service = inject(Base64Service);
  private readonly confirmService = inject(ConfirmService);

  public closedialog: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private readonly clrForm = viewChild(ClrForm);

  private opruefid: number;
  protected readonly selQuadrant = signal<0 | 1>(0);
  protected readonly quadrantCol = computed<Key<QuadrantWert>>(() =>
    this.selQuadrant() == 0 ? 'Quadrant1_2' : 'Quadrant3_4'
  );
  protected readonly selIndex = signal<number>(null);
  protected readonly isCameraOpen = signal(false);
  protected readonly isBildModalOpen = signal(false);
  protected readonly showSVGModal = signal(false);
  protected readonly bildData = signal<string>(null);

  private readonly bueResource = rxResource({
    stream: () => this.apiservice.getBUE(this.opruefid),
    defaultValue: {},
  });
  protected readonly bueMap = this.bueResource.value;
  protected readonly isLoadingBue = this.bueResource.isLoading;

  private readonly bilderResource = rxResource({
    stream: () => this.apiservice.getBUE_Bilder(this.opruefid),
  });
  protected readonly bilder = linkedSignal(() => this.bilderResource.value() ?? Array(8));
  protected readonly isLoadingBild = signal(false);

  set data(dataIn: any) {
    this.opruefid = dataIn.opruefid;
  }

  protected readonly formQuadrant = new FormGroupOfType<BUE_Quadrant>({
    bersa: new FormControl(null, Validators.required),
    vstmax: new FormControl(null, Validators.required),
    vstmin: new FormControl(null, Validators.required),
    ve: new FormControl(null, Validators.required),
    d: new FormControl(null, [Validators.required, Validators.min(3)]),
    sa: new FormControl(null, Validators.required),
  });

  constructor() {
    effect(() => {
      const map = this.bueMap();
      const quadrant = this.quadrantCol();

      if (this.showSVGModal())
        untracked(async () => {
          const value: BUE_Quadrant = parseQuadrant(map, quadrant);
          this.formQuadrant.reset(value, { emitEvent: false });
        });
    });

    this.formQuadrant.controls.bersa.valueChanges.pipe(takeUntilDestroyed()).subscribe(bersa => {
      if (bersa != this.formQuadrant.value.bersa)
        this.formQuadrant.reset(getDefaultQuadrant(bersa), { emitEvent: false });
    });
    this.formQuadrant.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      const sa = calculateSA(value);
      this.formQuadrant.controls.sa.setValue(sa, { emitEvent: false });
    });
  }

  initBue() {
    this.selQuadrant.set(0);
    this.showSVGModal.set(true);
  }

  protected async saveBue() {
    if (this.formQuadrant.invalid) {
      this.clrForm().markAsTouched();
      return;
    }
    try {
      await firstValueFrom(this.apiservice.setBUE(this.opruefid, this.quadrantCol(), this.formQuadrant.getRawValue()));
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      this.bueResource.reload();
    } catch (ex) {
      this.toastr.error(errorHandle(ex));
    }
  }

  protected async onTakenFoto(urlString: string) {
    await this.sendBUE_Bild(urlString);
  }

  protected async onFileChange(event: Event) {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files[0];
    fileInput.value = null;
    if (!file) return;

    if (file.size / 1024 / 1024 > 50) {
      this.toastr.error(
        file.name + this.mrTranslate.transform(' kann nicht geladen werden!'),
        this.mrTranslate.transform('Dateigröße')
      );
      return;
    }

    if (!file.type.includes('image')) {
      this.toastr.error(
        file.name + this.mrTranslate.transform(' kann nicht geladen werden!'),
        this.mrTranslate.transform('Dateityp')
      );
      return;
    }

    const urlString = await this.base64service.getUrlStringFromFile(file);
    await this.sendBUE_Bild(urlString);
  }

  private async sendBUE_Bild(urlString: string) {
    try {
      this.isLoadingBild.set(true);
      const sendObj = {
        base64: urlString.split(',').pop(),
        index: this.selIndex(),
      };
      const { success, error } = await firstValueFrom(this.apiservice.setBUE_Bild(this.opruefid, sendObj));
      if (success) {
        this.toastr.success(this.mrTranslate.transform('Bild gespeichert'));
        this.bilder.update(bilder => ((bilder[this.selIndex()] = urlString), [...bilder]));
      } else {
        this.toastr.error(error, this.mrTranslate.transform('Fehler beim Speichern'));
      }
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Fehler beim Speichern'));
    } finally {
      this.isLoadingBild.set(false);
    }
  }

  protected async deleteBILDBue(index: any) {
    this.selIndex.set(index);
    this.isLoadingBild.set(true);
    try {
      const text = this.mrTranslate.transform('Bild wirklich löschen?');
      const ok = await this.confirmService.danger(text);
      if (!ok) return;
      const { success, error } = await firstValueFrom(this.apiservice.deleteBUE_Bild(this.opruefid, index));
      if (success) {
        this.bilder.update(bilder => ((bilder[this.selIndex()] = null), [...bilder]));
        this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      } else {
        this.toastr.error(error, this.mrTranslate.transform('Fehler beim Löschen'));
      }
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Fehler beim Löschen'));
    } finally {
      this.isLoadingBild.set(false);
    }
  }

  protected close() {
    this.closedialog.next(true);
  }

  protected async createExcelDoc() {
    const zuschlag = [9, 9, 10, 13, 19];
    const faktor = [.09, .12, .18, .36, .72];
    const list = this.bueMap();
    let ta : number = 0.0;
    let vms: number = 0.0;
    let stBezeichnung = list["bersa"].Quadrant1_2;
    let stBezirk = "";

    const vStMin = +list["vstmin"].Quadrant1_2 || 10;
    const vStMinGegen = +list["vstmin"].Quadrant3_4 || 10;
    const vStMax = +list["vstmax"].Quadrant1_2 || 50;
    const vStMaxGegen = +list["vstmax"].Quadrant3_4 || 50;
    const veMax = +list["ve"].Quadrant1_2 || 50;
    const veMaxGegen = +list["ve"].Quadrant3_4 || 50;
    const breiteL = +list["sa"].Quadrant1_2 || 6;
    const breiteR = +list["sa"].Quadrant3_4 || 6;

    const res = await firstValueFrom(this.apiservice.getBUEExcelTemplate());
    const file = Buffer.from(res, 'base64');

        const wb = new ExcelJS.Workbook();
        wb.xlsx.load(file.buffer).then(() => {
          const ws = wb.worksheets[0];
          ws.getCell('E5').value = stBezeichnung;
          ws.getCell('D7').value = stBezirk;
          ws.getCell('C16').value = breiteR;
          ws.getCell('C21').value = breiteL;
          ws.getCell('C27').value = breiteL;
          ws.getCell('C32').value = breiteR;
          ws.getCell('E15').value = veMax;
          ws.getCell('E26').value = veMaxGegen;

          // D-Zeile = Vst; F-Zeile = sa
          let index = -1;
          for (var i = 16; i <= 36; i++) {
            if (i != 26) // Trenner-Zeile
            {
              index += 1;
              if (index > 4)
                index = 0;

              let vstVal = ws.getCell("D" + i.toString()).value.toString();
              if (vstVal == "50/40")
                vstVal = "50 u. 40";

              if (i <= 20 || i >= 32) {
                ta = zuschlag[index] + breiteR * faktor[index];
              }
              else {
                ta = zuschlag[index] + breiteL * faktor[index];
              }

              vms = i > 26 ? (veMaxGegen) / 3.6: (veMax) / 3.6;

              ws.getCell("F" + i.toString()).value = (ta * vms).toFixed(0);
            }
          }
          wb.xlsx.writeBuffer().then((buffer) => {
            this.apiservice.downloadBlobAsFile(buffer, "Bahnübergang_" + stBezeichnung + "_" + stBezirk + ".xlsx");
          });
        });
  }
  
}

export interface QuadrantWert {
  Quadrant1_2: string | null;
  Quadrant3_4: string | null;
}

export type BUE_Map = Partial<Record<keyof BUE_Quadrant, QuadrantWert>>;

export interface BUE_Quadrant {
  bersa: 'Fußgänger' | 'Kraftfahrzeug';
  vstmax: number;
  vstmin: number;
  ve: number;
  d: number;
  sa: number;
}

function parseQuadrant(map: BUE_Map, q: Key<QuadrantWert>): BUE_Quadrant {
  if (!map.sa?.[q]) return QUADRANT_DEFAULT_K;

  const bersa = (map.bersa[q] as BUE_Quadrant['bersa']) || 'Kraftfahrzeug';
  const vstmax = +map.vstmax[q];
  const vstmin = +map.vstmin[q];

  const maxRange = getVstMaxRange(bersa);
  const minRange = getVstMinRange(bersa);

  return {
    bersa,
    vstmax: maxRange.includes(vstmax) ? vstmax : null,
    vstmin: minRange.includes(vstmin) ? vstmin : null,
    ve: +map.ve[q],
    d: +map.d[q],
    sa: +map.sa[q],
  };
}

function getVstMaxRange(bersa: 'Fußgänger' | 'Kraftfahrzeug') {
  switch (bersa) {
    case 'Fußgänger':
      return [1.4, 1];
    case 'Kraftfahrzeug':
      return [50, 30, 20, 10, 5];
  }
}

function getVstMinRange(bersa: 'Fußgänger' | 'Kraftfahrzeug') {
  switch (bersa) {
    case 'Fußgänger':
      return [1.4, 1];
    case 'Kraftfahrzeug':
      return [10, 5];
  }
}

function getDefaultQuadrant(bersa: 'Fußgänger' | 'Kraftfahrzeug') {
  switch (bersa) {
    case 'Fußgänger':
      return QUADRANT_DEFAULT_F;
    case 'Kraftfahrzeug':
      return QUADRANT_DEFAULT_K;
  }
}

function calculateSA(value: Partial<BUE_Quadrant>): number {
  const { bersa, vstmax, vstmin, d, ve } = value;
  if (!(vstmax && vstmin && d && ve)) return null;

  let la = 0;
  let zuschlag = 0;
  let faktor = 0;

  switch (vstmax) {
    case 1: {
      la = 0;
      faktor = 1;
      zuschlag = 2;
      break;
    }
    case 1.4: {
      la = 0;
      faktor = 0.71;
      zuschlag = 2;
      break;
    }
    case 5: {
      la = 4;
      faktor = 0.72;
      zuschlag = 19;
      break;
    }
    case 10: {
      la = 6;
      faktor = 0.36;
      zuschlag = 13;
      break;
    }
    case 20: {
      la = 16;
      faktor = 0.18;
      zuschlag = 10;
      break;
    }
    case 30: {
      la = 22;
      faktor = 0.12;
      zuschlag = 9;
      break;
    }
    case 50: {
      la = 41;
      faktor = 0.09;
      zuschlag = 9;
      break;
    }
  }

  const ta = zuschlag + d * faktor;
  const vms = ve / 3.6;
  const sacal = ta * vms;

  const n = ve < 20 || bersa == 'Fußgänger' ? 1 : 5;
  const sa = getRoundedNumber(sacal, n);

  return sa;
}

function getRoundedNumber(number: number, multiplier: number) {
  let nearestValue = parseInt((number / multiplier).toString()) * multiplier;
  if (nearestValue >= number) {
    return nearestValue;
  } else {
    return nearestValue + multiplier;
  }
}

const QUADRANT_DEFAULT_K: BUE_Quadrant = {
  bersa: 'Kraftfahrzeug',
  vstmax: 50,
  vstmin: 10,
  ve: null,
  d: 20,
  sa: null,
};

const QUADRANT_DEFAULT_F: BUE_Quadrant = {
  bersa: 'Fußgänger',
  vstmax: 1,
  vstmin: 1,
  ve: null,
  d: 20,
  sa: null,
};
